import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle2, Mail, FileText, ArrowRight, Home, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';

const PaiementSucces = () => {
  const [searchParams] = useSearchParams();
  const commandeId = searchParams.get('commande');
  const [showConfetti, setShowConfetti] = useState(true);

  useEffect(() => {
    // Cacher les confettis après 3 secondes
    const timer = setTimeout(() => setShowConfetti(false), 3000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-green-50 flex items-center justify-center p-4">
      {/* Confetti effect */}
      {showConfetti && (
        <div className="fixed inset-0 pointer-events-none overflow-hidden">
          {[...Array(50)].map((_, i) => (
            <div
              key={i}
              className="absolute animate-fall"
              style={{
                left: `${Math.random() * 100}%`,
                top: '-20px',
                animationDelay: `${Math.random() * 2}s`,
                animationDuration: `${2 + Math.random() * 2}s`,
              }}
            >
              <div
                className="w-3 h-3 rounded-full"
                style={{
                  backgroundColor: ['#10B981', '#34D399', '#6EE7B7', '#A7F3D0', '#059669'][Math.floor(Math.random() * 5)],
                }}
              />
            </div>
          ))}
        </div>
      )}

      <div className="max-w-lg w-full">
        <div className="bg-white rounded-3xl shadow-xl p-8 text-center relative overflow-hidden">
          {/* Decorative background */}
          <div className="absolute top-0 right-0 w-40 h-40 bg-emerald-100 rounded-full -translate-y-1/2 translate-x-1/2 opacity-50" />
          <div className="absolute bottom-0 left-0 w-32 h-32 bg-green-100 rounded-full translate-y-1/2 -translate-x-1/2 opacity-50" />
          
          <div className="relative">
            {/* Success icon */}
            <div className="w-24 h-24 bg-gradient-to-br from-emerald-400 to-green-500 rounded-full mx-auto flex items-center justify-center mb-6 shadow-lg shadow-emerald-200">
              <CheckCircle2 className="w-12 h-12 text-white" />
            </div>

            <h1 className="text-3xl font-bold text-slate-900 mb-3">
              Paiement réussi !
            </h1>
            
            <p className="text-slate-600 mb-8">
              Votre mise en demeure va être envoyée en recommandé électronique (Clearbus) 
              dans les prochaines minutes.
            </p>

            {/* Order details */}
            <div className="bg-slate-50 rounded-2xl p-6 mb-8 text-left">
              <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-emerald-600" />
                Récapitulatif
              </h3>
              
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Service</span>
                  <span className="font-medium text-slate-900">Mise en demeure Clearbus</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Montant payé</span>
                  <span className="font-bold text-emerald-600">8,90 €</span>
                </div>
                {commandeId && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">N° de commande</span>
                    <span className="font-mono text-xs text-slate-600">{commandeId.slice(0, 8)}...</span>
                  </div>
                )}
              </div>
            </div>

            {/* Next steps */}
            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6 mb-8 text-left">
              <h3 className="font-semibold text-emerald-900 mb-3 flex items-center gap-2">
                <Sparkles className="w-5 h-5" />
                Prochaines étapes
              </h3>
              
              <ol className="space-y-3 text-sm text-emerald-800">
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-emerald-200 rounded-full flex items-center justify-center text-emerald-700 font-bold text-xs">1</span>
                  <span>Le destinataire recevra un email avec un lien sécurisé pour consulter la lettre</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-emerald-200 rounded-full flex items-center justify-center text-emerald-700 font-bold text-xs">2</span>
                  <span>Vous recevrez l'accusé de réception par email dès qu'il aura consulté la lettre</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-emerald-200 rounded-full flex items-center justify-center text-emerald-700 font-bold text-xs">3</span>
                  <span>Conservez l'accusé de réception comme preuve juridique</span>
                </li>
              </ol>
            </div>

            {/* Email notification */}
            <div className="flex items-center justify-center gap-2 text-sm text-slate-500 mb-8">
              <Mail className="w-4 h-4" />
              <span>Un email de confirmation vous a été envoyé</span>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/tableau-de-bord" className="flex-1">
                <Button className="w-full bg-emerald-500 hover:bg-emerald-600 text-white">
                  <Home className="w-4 h-4 mr-2" />
                  Retour au tableau de bord
                </Button>
              </Link>
              <Link to="/generateur-mise-en-demeure" className="flex-1">
                <Button variant="outline" className="w-full border-emerald-300 text-emerald-700 hover:bg-emerald-50">
                  Nouvelle mise en demeure
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Footer note */}
        <p className="text-center text-xs text-slate-400 mt-6">
          Une question ? Contactez-nous à gmpdeveloppement@gmail.com
        </p>
      </div>

      <style>{`
        @keyframes fall {
          0% {
            transform: translateY(0) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(720deg);
            opacity: 0;
          }
        }
        .animate-fall {
          animation: fall linear forwards;
        }
      `}</style>
    </div>
  );
};

export default PaiementSucces;
